import React, { useState, useContext, useEffect } from "react";
import { AppPage, NoIconAlert } from "../../components";
import { useTranslate, useYearInTitle, sendTealiumEvent } from "../../helpers";
import { Button, Choice } from "@cmsgov/ds-medicare-gov";
import Routes from "../../app/routes";
import { PharmacyType, ActionType, PDE } from "../../@types";
import { AppStore } from "../../app/store";
import { RouteComponentProps, withRouter } from "react-router";
import { createGa4Event, Ga4Event } from "../../helpers/analytics";
import { useFlags } from "launchdarkly-react-client-sdk";

interface NextPageProps {
  drugCosts: string;
  pdes: PDE[] | undefined;
  search: string;
}

// Pulled this out into a standalone function in order to make testing easier
export const determineNextPage = ({
  drugCosts,
  pdes,
  search,
}: NextPageProps): string => {
  let uri;

  if (drugCosts === "include") {
    if (pdes && pdes.length) {
      uri = `${Routes.pdePage}${search}`;
    } else {
      uri = `${Routes.managePrescriptions}${search}`;
    }
  } else {
    uri = `${Routes.searchResults}${search}`;
  }

  return uri;
};

const DrugSearchPreferencesPage: React.FunctionComponent<RouteComponentProps> = props => {
  const [drugCosts, setDrugCosts] = useState("");
  const t = useTranslate();
  const { dispatch, state } = useContext(AppStore.AppContext);
  const flags = useFlags();

  const showContinueButton = drugCosts === "exclude" || drugCosts === "include";

  useEffect(() => {
    dispatch({ type: ActionType.RESET_PHARMACIES });
  }, []);

  /* istanbul ignore next: tested elsewhere */
  const goToNextPage = (): void => {
    const uri = determineNextPage({
      drugCosts,
      pdes: state.beneficiary ? state.beneficiary.pdes : [],
      search: props.location.search,
    });

    createGa4Event({ event_name: Ga4Event.DRUG_SEARCH_PREF, state, flags });

    props.history.push(uri);
  };

  // heading is the shown on the page, i.e. <h1>.
  const heading = t("plan_search_prefs.header");
  // title is the page title.
  const title = useYearInTitle(heading, state.year);

  return (
    <AppPage heading={heading} title={title}>
      <div className="DrugSearchPreferencePage ds-u-margin-bottom--3">
        <span className="ds-u-visibility--screen-reader">
          {t("sr_only_additional_questions_may_appear")}
        </span>
        <fieldset className="ds-c-fieldset ds-u-margin--0">
          <legend className="ds-u-margin--0 ds-text-heading--xl ds-u-margin-bottom--1 ds-u-padding--0">
            {t("plan_search_prefs.include_drug_costs")}
          </legend>

          <Choice
            type="radio"
            name="drugCosts"
            value="include"
            className="e2e-drug-search-pref--yes"
            checked={drugCosts === "include"}
            onChange={(): void => {
              // istanbul ignore next: tested elsewhere
              sendTealiumEvent("Find Plans - See Drug Costs", "Yes");
              setDrugCosts("include");

              createGa4Event({
                event_name: Ga4Event.RADIO_BUTTON_SELECTED,
                heading: t("plan_search_prefs.include_drug_costs"),
                text: t("yes"),
                state,
                flags,
              });
            }}
            checkedChildren={NoIconAlert(
              t("plan_search_prefs.great"),
              t("plan_search_prefs.to_see_drug_costs")
            )}
          >
            {t("yes")}
          </Choice>

          <Choice
            type="radio"
            name="drugCosts"
            value="exclude"
            className="e2e-drug-search-pref--no"
            checked={drugCosts === "exclude"}
            onChange={(): void => {
              // istanbul ignore next: tested elsewhere
              sendTealiumEvent("Find Plans - See Drug Costs", "No");
              dispatch({
                type: ActionType.UPDATE_PHARMACY_TYPE,
                payload: PharmacyType.NONE,
              });
              setDrugCosts("exclude");

              createGa4Event({
                event_name: Ga4Event.RADIO_BUTTON_SELECTED,
                heading: t("plan_search_prefs.include_drug_costs"),
                text: t("no"),
                state,
                flags,
              });
            }}
            checkedChildren={NoIconAlert(
              t("plan_search_prefs.are_you_sure"),
              t("plan_search_prefs.need_to_enter_drugs")
            )}
          >
            {t("no")}
          </Choice>
        </fieldset>

        {showContinueButton && (
          <section className="ContinueSection">
            <Button
              className="e2e-drug-search-pref--continue mct-u-width--full mct-u-md-width--auto"
              variation="primary"
              onClick={goToNextPage}
            >
              {t("continue")}
            </Button>
          </section>
        )}
      </div>
    </AppPage>
  );
};

export default withRouter(DrugSearchPreferencesPage);
